import { Parser } from 'htmlparser2'

/**
 * Presets for sanitization configuration for different contexts
 * Centralizes security policies for HTML
 */
export const HTML_SANITIZE_PRESETS = {
  // Only text, remove all HTML
  TEXT_ONLY: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  },

  // Basic formatting (bold, italic, line break)
  BASIC_FORMAT: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'span'],
    ALLOWED_ATTR: ['class'],
  },

  // With links
  WITH_LINKS: {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'span', 'a', 'p'],
    ALLOWED_ATTR: ['href', 'class'],
  },

  // Rich text with lists (FAQ, structured content)
  RICH_TEXT: {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: ['href', 'class', 'id', 'colspan', 'rowspan', 'align', 'target'],
  },

  // Disclaimer and legal content
  DISCLAIMER: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'a', 'span'],
    ALLOWED_ATTR: ['href', 'class'],
  },

  // Parsed markdown
  MARKDOWN: {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
    ],
    ALLOWED_ATTR: ['href', 'class'],
  },
}

export type SanitizePreset = keyof typeof HTML_SANITIZE_PRESETS
export type DOMPurifyConfig = {
  ALLOWED_TAGS?: string[]
  ALLOWED_ATTR?: string[]
  [key: string]: unknown
}

type DOMPurifyInstance = {
  sanitize: (html: string, config?: DOMPurifyConfig) => string
  default?: DOMPurifyInstance
}

type DOMPurifyModule = DOMPurifyInstance | { default: DOMPurifyInstance }

let clientDOMPurify: DOMPurifyInstance | null = null

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
])

/** Elements whose content must be dropped entirely (DOMPurify-like). */
const DROP_CONTENTS = new Set([
  'script',
  'style',
  'noscript',
  'iframe',
  'object',
  'embed',
  'link',
  'meta',
])

const escapeText = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

const escapeAttr = (value: string): string =>
  escapeText(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;')

const isSafeHref = (value: string): boolean => {
  const trimmed = value.trim()
  if (!trimmed) return true
  return !/^(javascript|vbscript|data\s*:)/i.test(trimmed)
}

/**
 * SSR-safe allowlist sanitizer (htmlparser2).
 * Used during Gatsby build/SSR where jsdom/DOMPurify cannot run reliably.
 */
const sanitizeWithAllowlist = (html: string, config?: DOMPurifyConfig): string => {
  const allowedTags = new Set((config?.ALLOWED_TAGS as string[] | undefined) ?? [])
  const allowedAttr = new Set((config?.ALLOWED_ATTR as string[] | undefined) ?? [])
  const stripAllTags = Array.isArray(config?.ALLOWED_TAGS) && config.ALLOWED_TAGS.length === 0

  let output = ''
  let dropDepth = 0
  const openStack: string[] = []

  const parser = new Parser(
    {
      onopentag(name, attribs) {
        const tag = name.toLowerCase()

        if (DROP_CONTENTS.has(tag)) {
          dropDepth += 1
          return
        }
        if (dropDepth > 0 || stripAllTags || !allowedTags.has(tag)) {
          return
        }

        const attrs = Object.entries(attribs)
          .filter(([key]) => allowedAttr.has(key.toLowerCase()))
          .filter(([key, value]) => {
            if (key.toLowerCase() === 'href') return isSafeHref(value)
            if (key.toLowerCase() === 'style') return false
            return true
          })
          .map(([key, value]) => `${key}="${escapeAttr(value)}"`)
          .join(' ')

        output += attrs ? `<${tag} ${attrs}>` : `<${tag}>`
        if (!VOID_ELEMENTS.has(tag)) {
          openStack.push(tag)
        }
      },
      ontext(text) {
        if (dropDepth > 0) return
        output += escapeText(text)
      },
      onclosetag(name) {
        const tag = name.toLowerCase()

        if (DROP_CONTENTS.has(tag)) {
          dropDepth = Math.max(0, dropDepth - 1)
          return
        }
        if (dropDepth > 0 || stripAllTags || !allowedTags.has(tag) || VOID_ELEMENTS.has(tag)) {
          return
        }
        if (openStack[openStack.length - 1] === tag) {
          openStack.pop()
          output += `</${tag}>`
        }
      },
    },
    { decodeEntities: true },
  )

  parser.write(html)
  parser.end()

  while (openStack.length > 0) {
    output += `</${openStack.pop()}>`
  }

  return output
}

const resolveDOMPurifyExport = (dompurifyModule: DOMPurifyModule): DOMPurifyInstance =>
  'default' in dompurifyModule && dompurifyModule.default
    ? dompurifyModule.default
    : (dompurifyModule as DOMPurifyInstance)

const getClientDOMPurify = (): DOMPurifyInstance => {
  if (clientDOMPurify) {
    return clientDOMPurify
  }

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const dompurifyModule = require('dompurify') as DOMPurifyModule
  clientDOMPurify = resolveDOMPurifyExport(dompurifyModule)
  return clientDOMPurify
}

const sanitize = (html: string, config?: DOMPurifyConfig): string => {
  if (typeof window === 'undefined') {
    return sanitizeWithAllowlist(html, config)
  }
  return getClientDOMPurify().sanitize(html, config) as string
}

/**
 * Sanitize HTML with a predefined preset
 * @param html - HTML content to sanitize
 * @param preset - Name of the preset to use (default: BASIC_FORMAT)
 * @returns Safe HTML
 */
export const sanitizeHtml = (
  html: string | undefined,
  preset: SanitizePreset = 'BASIC_FORMAT',
): string => {
  if (!html) return ''
  return sanitize(html, HTML_SANITIZE_PRESETS[preset])
}

/**
 * Sanitize with custom configuration
 * @param html - HTML content to sanitize
 * @param customConfig - Custom DOMPurify configuration
 * @returns Safe HTML
 */
export const sanitizeHtmlCustom = (
  html: string | undefined,
  customConfig?: DOMPurifyConfig,
): string => {
  if (!html) return ''
  return sanitize(html, customConfig)
}
